import {BOUNDARY_PAD_SEC, MAX_PAD_SEC, MIN_GAP_AFTER_CUT_SEC, MIN_PAD_SEC} from "./fillerWord";

/**
 * Compute how much padding to apply around a filler word.
 * The padding depends on how much natural gap exists between neighboring words
 * AFTER the filler is removed.
 *
 * Key idea: extend the cut to fully remove the filler, but not so much
 * that we slam the adjacent words together.
 * 
 * After cutting filler + 2*padding, the remaining gap is:
 *   rawGap - fillerDuration - 2*padding >= MIN_GAP_AFTER_CUT_SEC
 * 
 * @param fillerStartSec - Start time of the filler word
 * @param fillerEndSec - End time of the filler word
 * @param prevWordEndSec - End time of the word before the filler
 * @param nextWordStartSec - Start time of the word after the filler
 */
export function computeFillerPaddingSec(
    fillerStartSec: number,
    fillerEndSec: number,
    prevWordEndSec: number | null,
    nextWordStartSec: number | null
): number {
    // At start/end of transcript, we can be more aggressive
    if (prevWordEndSec === null || nextWordStartSec === null) {
        return BOUNDARY_PAD_SEC;
    }

    const rawGap = nextWordStartSec - prevWordEndSec;
    const fillerDuration = fillerEndSec - fillerStartSec;
    
    // Gap that would remain if we just removed the filler (no padding)
    const gapWithoutPadding = rawGap - fillerDuration;

    // If the gap without padding is already below minimum, no padding is safe
    if (gapWithoutPadding <= MIN_GAP_AFTER_CUT_SEC) {
        return 0;
    }

    // Maximum padding that still leaves MIN_GAP_AFTER_CUT_SEC:
    // gapWithoutPadding - 2*pad >= MIN_GAP_AFTER_CUT_SEC
    // pad <= (gapWithoutPadding - MIN_GAP_AFTER_CUT_SEC) / 2
    const maxPadFromGap = (gapWithoutPadding - MIN_GAP_AFTER_CUT_SEC) / 2;
    if (maxPadFromGap <= 0) {
        return 0;
    }

    // Clamp to global MAX, then require MIN_PAD
    const allowedByGapAndMax = Math.min(maxPadFromGap, MAX_PAD_SEC);

    if (allowedByGapAndMax < MIN_PAD_SEC) {
        return 0; // can't safely pad without violating min gap
    }

    return allowedByGapAndMax;
}